import React, { useRef } from 'react';
import { FaPenFancy, FaRocket, FaCheckCircle } from 'react-icons/fa';
import RoughBorder from '../../kits/LayoutKit/RoughBorder';
import MonoButton from '../../kits/DesignKit/MonoButton';

/**
 * Wiederverwendbare ZenPost Pricing-Section Komponente
 * Zeigt Pricing Cards mit RoughBorder und mobilem Design
 */
const ZenPostPricingSection = ({
  title = "Einfacher Preis. Keine Überraschungen.",
  subtitle = "ZenEditor dauerhaft kostenlos. Pro für Teams und Cloud.",
  plans = null,
  showEnterpriseContact = true,
  enterpriseText = "Enterprise oder Custom Plan?",
  enterpriseEmail = "saghallo@denisbitter.de",
  onCTAClick = null,
  theme = "light"
}) => {
  const pricingCard1Ref = useRef();
  const pricingCard2Ref = useRef();

  // Default Pricing Plans
  const defaultPlans = [
    {
      name: 'ZenEditor',
      price: 'Kostenlos',
      emoji: '✍️',
      icon: <FaPenFancy className="text-brand" />,
      description: 'Reines Schreiben im Web oder als Desktop-App',
      features: [
        'Reines Schreiben (Web oder Desktop)',
        'Lokale KI mit Ollama (optional)',
        'Lokale Datenspeicherung',
        'Kein Publisher',
        'Kein Doc Studio',
        '100% Offline',
        'Unbegrenzte Nutzung'
      ],
      cta: 'Live Demo starten',
      popular: false,
      bgColor: 'bg-gradient-to-br from-white via-brand/5 to-white',
      ref: pricingCard1Ref
    },
    {
      name: 'ZenPost Studio Pro',
      price: '39€',
      period: '/Monat',
      emoji: '🚀',
      icon: <FaRocket className="text-brand" />,
      description: 'Web + Desktop + Cloud-Sync + Doc Studio',
      features: [
        'Alles aus Desktop',
        'Cloud-Sync über alle Geräte',
        'Team-Kollaboration (bis 5 User)',
        'Analytics & Insights',
        'Custom Templates',
        'Priority Support',
        'Versionierung & History',
        'API-Zugriff (Basic)',
        'Doc Studio',
        'KI Anbindung an andere KI möglich'
      ],
      cta: 'Pro starten',
      popular: true,
      bgColor: 'bg-gradient-to-br from-white via-purple/5 to-white',
      ref: pricingCard2Ref
    }
  ];

  const pricingPlans = plans || defaultPlans;

  const isDark = theme === "dark";

  return (
    <section className={isDark ? "py-14 sm:py-20 px-4 text-white" : "py-12 sm:py-16 px-4 bg-gradient-to-b from-white to-gray-50"}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 px-4">
          <div className={isDark ? "inline-flex items-center rounded-full border border-brand px-3 py-1 text-xs font-semibold text-white" : "hidden"}>
            Kosten
          </div>
          <h2 className={isDark ? "mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white" : "text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4"}>
            {title}
          </h2>
          <p className={isDark ? "mt-3 text-base sm:text-lg text-slate-300 max-w-3xl mx-auto" : "text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"}>
            {subtitle}
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12 px-4">
          {pricingPlans.map((plan, index) => {
            const cardRef = index === 0 ? pricingCard1Ref : pricingCard2Ref;
            return (
              <div key={index} ref={cardRef} className="relative h-full">
                <div className={isDark ? "relative overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-950/60 p-6 sm:p-8 shadow-xl shadow-slate-950/40 h-full flex flex-col" : "bg-gradient-to-br from-white via-brand/5 to-white p-6 sm:p-8 rounded-lg shadow-lg transition-all hover:shadow-xl h-full flex flex-col"}>
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className={isDark ? "absolute right-6 top-6 rounded-full bg-fuchsia-500/20 px-3 py-1 text-xs font-semibold text-brand border border-brand" : "mb-3 sm:mb-0"}>Most Popular</div>
                  )}

                  {/* Icon Header - NUR FA Icon, kein Emoji */}
                  <div className="mb-3 sm:mb-4">
                    <div className={isDark ? "text-4xl sm:text-5xl text-fuchsia-300" : "text-4xl sm:text-5xl"}>{plan.icon}</div>
                  </div>

                  {/* Plan Name */}
                  <h3 className={isDark ? "text-xl sm:text-2xl md:text-3xl font-extrabold text-white mb-2 sm:mb-3" : "text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-2 sm:mb-3"}>
                    {plan.name}
                  </h3>

                  {/* Description - Fixed Height */}
                  <p className={isDark ? "text-sm sm:text-base text-slate-300 mb-4 sm:mb-6 min-h-[3rem]" : "text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 min-h-[3rem]"}>
                    {plan.description}
                  </p>

                  {/* Price */}
                  <div className="mb-4 sm:mb-6">
                    <span className={isDark ? "text-4xl sm:text-5xl md:text-6xl font-extrabold text-white" : "text-4xl sm:text-5xl md:text-6xl font-black text-gray-900"}>
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className={isDark ? "text-base sm:text-lg text-slate-400 ml-1" : "text-base sm:text-lg text-gray-600 ml-1"}>
                        {plan.period}
                      </span>
                    )}
                  </div>

                  {/* Features List - Fixed Min Height */}
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 min-h-[18rem] md:min-h-[20rem]">
                    {plan.features.map((feature, i) => (
                      <li key={i} className={isDark ? "flex items-start gap-2 text-xs sm:text-sm text-brand" : "flex items-start gap-2 text-xs sm:text-sm text-gray-700"}>
                        <FaCheckCircle className={isDark ? "text-brand flex-shrink-0 mt-0.5" : "text-brand flex-shrink-0 mt-0.5"} />
                        <span className="flex-shrink-0">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button - At Bottom */}
                  <div className="mt-auto">
                    <MonoButton
                      onClick={() => {
                        if (onCTAClick) onCTAClick(plan);
                        if (plan.ctaAction) plan.ctaAction();
                      }}
                      className={isDark ? (plan.popular ? "w-full rounded-full border-transparent bg-gradient-to-r from-fuchsia-500 to-violet-500 px-4 py-3 text-sm sm:text-base font-semibold text-brand shadow-lg shadow-fuchsia-500/30" : "w-full rounded-full border border-slate-600/60 bg-slate-900/60 px-4 py-3 text-sm sm:text-base font-semibold text-slate-200") : "w-full py-3 text-sm sm:text-base"}
                      underlineColors={isDark ? (plan.popular ? "ZenBrand" : "ZenBrand") : (plan.popular ? "orange" : "brand")}
                    >
                      {plan.cta}
                    </MonoButton>
                  </div>
                </div>

                {/* RoughBorder */}
                {!isDark && (
                  <RoughBorder
                    parentRef={cardRef}
                    radius={10}
                    padding={2}
                    color={plan.popular ? "#AC8E66" : "#AC8E66"}
                    strokeWidth={plan.popular ? 0.4 : 0.3}
                    roughness={1}
                    bowing={2}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Enterprise Contact */}
        {showEnterpriseContact && (
          <div className="text-center px-4">
            <p className={isDark ? "text-sm sm:text-base text-slate-400" : "text-sm sm:text-base text-gray-600"}>
              {enterpriseText}{' '}
              <a
                href={`mailto:${enterpriseEmail}?subject=ZenPost Studio Enterprise`}
                className={isDark ? "text-brand font-semibold hover:underline" : "text-brand font-bold hover:underline"}
              >
                Lass uns sprechen →
              </a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ZenPostPricingSection;
